import { useState, useEffect, useCallback } from 'react';

// Custom hook for API calls with loading states
export const useApi = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
      throw err;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Custom hook for form submissions
export const useApiSubmit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(async (apiFunction, data) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const result = await apiFunction(data);
      setSuccess(true);
      return result;
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
    setLoading(false);
  }, []);

  return { submit, loading, error, success, reset };
};

// Custom hook for testimonials
export const useTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTestimonials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Import here to avoid circular dependency
      const { testimonialService } = await import('../services/api');
      const result = await testimonialService.getTestimonials();
      setTestimonials(result);
    } catch (err) {
      console.warn('API testimonials not available, using fallback data:', err.message);
      setError(err.message);
      // Fallback to mock data if API fails
      try {
        const { testimonials: mockTestimonials } = await import('../data/mock');
        setTestimonials(mockTestimonials);
      } catch (mockError) {
        console.error('Failed to load mock testimonials:', mockError);
        setTestimonials([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  return { testimonials, loading, error, refetch: fetchTestimonials };
};

// Custom hook for appointment slots
export const useAppointmentSlots = (date = null) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { appointmentService } = await import('../services/api');
      const result = await appointmentService.getAvailableSlots(date);
      setSlots(result);
    } catch (err) {
      console.warn('API appointment slots not available, using fallback data:', err.message);
      setError(err.message);
      // Fallback to mock data if API fails
      try {
        const { appointmentSlots } = await import('../data/mock');
        setSlots(appointmentSlots || []);
      } catch (mockError) {
        console.error('Failed to load mock appointment slots:', mockError);
        setSlots([]);
      }
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  return { slots, loading, error, refetch: fetchSlots };
};

export default useApi;